// Data plane for the roster planner board.
//
// Talks to the existing /api/rosters/* endpoints (no schema changes needed
// for read paths) and shapes the response into Planby's channel+epg model.
// Auth follows the same localStorage-token convention used by the rest of
// the rosters module: a raw fetch with an explicit Authorization header, NOT
// the axios interceptor the rest of the app uses. (This used to point at
// RosterAssignments.tsx as the reference; that screen was unreachable and has
// been deleted.)

import { type LeaveCode, LEGEND } from '../../../constants/rosterLegend';

export interface PlannerSite {
  _id?: string;
  siteCode: string;
  siteName?: string;
  siteDisplayName?: string;
  isActive?: boolean;
  timezone?: string;
  metadata?: { region?: string; address?: string };
  /** Admins who have claimed this site — used to scope tabs to "my sites". */
  administrators?: Array<{ userId?: string; email?: string; name?: string }>;
  /** The site's roster runs in GRAutomates (Rosters → Sites → Live). */
  isLive?: boolean;
  liveAt?: string;
  liveBy?: string;
}

export interface PlannerCamp {
  _id: string;
  siteCode: string;
  campCode: string;
  name: string;
  /** Designated fill colour (hex) — tints the camp cell when assigned. */
  color?: string | null;
  area?: string;
  location?: string;
  specialRequest?: string;
  capacity?: number;
  isActive?: boolean;
}

/** One admin-pre-coded Job/WC/AC split for a roster cell or pattern segment.
 *  Mirrors a Timesheet line's coding shape. Admin-only planning data — never a
 *  timesheet write; not returned in worker payloads. */
export interface TimesheetCodingLine {
  job?: string;
  jobName?: string;
  workCentre?: string;
  workCentreName?: string;
  activityCode?: string;
  activityCodeDesc?: string;
  qty?: number;
  qtyBasis?: 'hours' | 'days';
}

export interface PlannerPattern {
  _id: string;
  patternName: string;
  patternCode: string;
  rotationDays: number;
  cycleLength?: number;
  swingVocab?: string[];
  /** Site codes this pattern applies to. EMPTY means every site — see
   *  patternsForSite(); it is empty on every pattern in production. */
  applicableSites?: string[];
  isActive?: boolean;
  segments?: Array<{
    segmentId: string;
    segmentName: string;
    days: number;
    shiftType: 'work' | 'travel' | 'off';
    color?: string;
    abbreviation?: string;
    /** Default forecast hours for each day in this segment (H1). */
    hoursPerDay?: number;
    /** Day vs night working (S1) — only meaningful for work segments. */
    workShift?: 'day' | 'night';
    /** Default Job/WC/AC coding for work days in this segment. */
    defaultCoding?: TimesheetCodingLine[];
  }>;
}

/** Someone else with this site's planner open. */
export interface RosterPresencePeer {
  userName?: string;
  userEmail?: string;
  lastSeenAt?: string;
}

export interface PlannerGroup {
  _id: string;
  groupName: string;
  groupCode: string;
  siteCode: string;
  /** 0 = Group (parent), 1 = Team (child). Legacy groups omit it. */
  level?: number;
  parentGroupId?: string | { _id: string } | null;
  /** Team colour (level 1 only) — tints the team's workers' camp cells. */
  color?: string | null;
  /** The rotation this Group/Team runs, populated by the groups endpoint with
   *  `patternName`/`patternCode`. Moving somebody onto a Team whose rotation
   *  differs from the one painted on their cells is what drives the
   *  "re-apply the pattern?" prompt on the planner. */
  shiftPatternId?: string | { _id: string; patternName?: string; patternCode?: string } | null;
  /** Manual display order within the site (lower = higher in the planner). */
  displayOrder?: number;
  members?: Array<{
    employeeId: string;
    isActive?: boolean;
    position?: string;
    /** Manual position within the group. Undefined = never ordered by hand,
     *  which sorts after those that were, alphabetically. */
    displayOrder?: number;
  }>;
}

/**
 * The Excel-only overlay carried by RosterEmployeeProfile. Returned
 * inside PlannerEmployee.profile when set.
 */
export interface PlannerEmployeeProfile {
  travelMode?: 'FIFO' | 'DIDO' | 'LOCAL';
  travelId?: string;
  teamCode?: string;
  timesheetCode?: string;
  permanentRoomNo?: string;
  /** Room used on OVERCYCLE (`OC`) days only. Blank falls back to
   *  permanentRoomNo -- see roomForDay(), the single resolver. */
  overcycleRoomNo?: string;
  swingTeam?: string;
  /** Free-text roster-type label from the admins' Excel column (e.g. "2n2" =
   *  2 weeks on/2 off, "3n3" = 3 on/3 off). Display-only; the Inactive toggle
   *  (PlannerEmployee.isActive) controls visibility, not this label. */
  rosterType?: string;
  notes?: string;
  /** Contractor registry fields (C1) — maintained for external people. */
  company?: string;
  email?: string;
  phone?: string;
  contactNotes?: string;
  /** Only sent to people who can edit the site's roster; never to viewers. */
  adminNotes?: string;
  adminNotesUpdatedBy?: string;
  adminNotesUpdatedAt?: string;
  source?: 'manual' | 'import:csv' | 'import:xlsx' | 'migration';
  /** Free-text values for the site's admin-defined custom columns,
   *  keyed by RosterSite.customColumns[].key. */
  customFields?: Record<string, string>;
}

/** Admin-defined extra matrix column for a site (Task 8). */
export interface PlannerCustomColumn {
  key: string;
  label: string;
  width?: number;
  order?: number;
}

/** Admin-defined custom cell code/colour, bound to a site (feedback #4/#5).
 *  Shown in the planner legend alongside the built-in codes. */
export interface PlannerCustomCode {
  key: string;
  label: string;
  abbreviation: string;
  fillHex: string;
  coverageRequired?: boolean;
  /** The code marks a day on site, for minimum manning (a site's own codes are
   *  otherwise read as off site, like an office). */
  onSite?: boolean;
  order?: number;
  /** Paint-palette grouping: 'general' (site codes), 'camp', 'training', or
   *  'unknown' (imported, not yet classified). A roster cell-paint grouping
   *  only — unrelated to the Training module. */
  section?: 'general' | 'camp' | 'training' | 'unknown';
}

export interface PlannerEmployee {
  employeeId: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  /** True for manually-added non-GreenTree people (subcontractors, EXT- ids). */
  isExternal?: boolean;
  /** True when the person is a deliberate member of this site's roster (R1) —
   *  either added explicitly or holding a non-cancelled assignment (after a
   *  removal, one with a day from the removal on: the earlier days are kept as
   *  history). Drives the Rostered/Unrostered filter and the member badge,
   *  independent of patterns. */
  isMember?: boolean;
  /** Active on this site's roster (default true). Inactive people are hidden
   *  from the day-to-day board but stay counted in the man-hours forecast. */
  isActive?: boolean;
  position?: string;
  positionCode?: string;
  functionCode?: string;
  assignedTeam?: string;
  email?: string;
  site?: string;
  profitCentreCode?: string;
  profitCentreName?: string;
  payGroup?: string;
  /** Per-(employeeId, siteCode) Excel-only overlay. Null when no row
   *  has been imported / created for this employee at this site yet. */
  profile?: PlannerEmployeeProfile | null;
}

export interface PlannerSiteDirectoryEntry {
  siteCode: string;
  displayName: string;
  region: string;
  address?: string;
  /** Number of sub-site rows beneath this parent in OrgSiteMapping
   *  (e.g. Santos 32 has 3: 32-SB, 32-CB, 32-BNE). The picker only
   *  surfaces parents; sub-sites are pulled in automatically when the
   *  employees endpoint expands the site code server-side. */
  subSiteCount?: number;
  claimed: boolean;
  /** The site's roster runs in GRAutomates (Rosters → Sites → Live). */
  isLive?: boolean;
  administrators: Array<{
    userId?: string;
    email?: string;
    name?: string;
    role?: string;
    addedAt?: string;
  }>;
  rosterSiteId?: string | null;
  /** Whether the current user can actually open this site's data. Undefined on
   *  responses from an older backend — treat as accessible in that case. */
  canAccess?: boolean;
}

export interface ProfileImportRow {
  surname: string;
  firstName: string;
  travel?: string;
  travelId?: string;
  team?: string;
  timesheetCode?: string;
  permanentRoomNo?: string;
  overcycleRoomNo?: string;
  roster?: string;
}

export interface ProfileImportResult {
  batchId: string;
  imported: number;
  skipped: Array<{
    rowIndex: number;
    surname: string;
    firstName: string;
    reason: string;
  }>;
  errors: Array<{
    rowIndex: number;
    surname: string;
    firstName: string;
    error: string;
  }>;
  importedSample: Array<{
    rowIndex: number;
    surname: string;
    firstName: string;
    employeeId: string;
  }>;
}

export interface PaintUpdate {
  employeeId: string;
  /** ISO timestamp; will be normalised to midnight UTC server-side. */
  date: string;
  patch: Partial<PlannerShiftDay>;
  /**
   * The assignment the painted cell belongs to (real cells only). Lets the
   * server target the exact record the user sees instead of resolving by date
   * range — essential when a day is covered by more than one assignment so
   * erase/paint can't land on a hidden overlapping cell. Omitted for blank
   * cells, where the server lazy-creates / resolves by range.
   */
  assignmentId?: string;
}

export interface PaintResult {
  employeeId: string;
  date: string;
  ok: boolean;
  assignmentId?: string;
  error?: string;
}

export interface PlannerShiftDay {
  date: string; // ISO
  segmentId?: string;
  segmentName?: string;
  shiftType?: 'work' | 'travel' | 'off' | 'leave';
  color?: string;
  abbreviation?: string;
  /** Forecast hours rostered for this day (H1) — seeded from the pattern
   *  segment default, editable per cell. Roster-local (feeds the forecast). */
  hoursPerDay?: number;
  /** Day vs night working (S1) — seeded from the segment, toggled per cell. */
  workShift?: 'day' | 'night';
  /** Admin-only Job/WC/AC coding for this day (multiple lines allowed). Present
   *  only in admin/planner payloads — projected out of worker responses. */
  timesheetCoding?: TimesheetCodingLine[];
  /** Camp code where the worker is located on this day (free-text fallback,
   *  resolves to a RosterCamp on the site when the picker is used). */
  campCode?: string;
  /** Camp detail resolved server-side for My Shifts. The planner decodes camps
   *  from its own /camps fetch; an employee can't call that endpoint, so
   *  /my-shifts joins it in. Absent in planner payloads. */
  camp?: PlannerResolvedCamp;
  /** Label of the per-site custom paint code matching `abbreviation`, resolved
   *  server-side for My Shifts (same reason as `camp`). */
  codeLabel?: string;
  leaveCode?: LeaveCode;
  coverageRequired?: boolean;
  needsReview?: boolean;
  reviewNote?: string;
  acceptanceStatus?: 'pending' | 'accepted' | 'declined' | 'auto_accepted';
  /** Latest note (mirror of the most recent noteHistory entry). */
  notes?: string;
  /** Append-only note audit trail (Task 7) — newest entries last. */
  noteHistory?: PlannerCellNote[];
  /** Attachment references for this cell (Task 9). */
  documents?: PlannerCellDocument[];
  /** Last-edit provenance (shown in the cell modal). */
  modifiedBy?: string;
  modifiedAt?: string;
}

/** A roster notification addressed to the signed-in worker. */
export interface RosterNotificationRow {
  _id: string;
  notificationType: string;
  title: string;
  message: string;
  siteCode?: string;
  relevantDate?: string;
  // 'in_app_only' = the worker can see it in the app, but no email or push
  // actually went out (the outbound channels are off by default). It is just
  // as delivered as 'sent' as far as the UI is concerned.
  status: 'pending' | 'sent' | 'in_app_only' | 'read' | 'dismissed';
  createdAt?: string;
  sentAt?: string;
}

/** Camp detail as resolved onto a My Shifts cell. */
export interface PlannerResolvedCamp {
  campCode: string;
  name?: string;
  area?: string;
  location?: string;
  specialRequest?: string;
}

export interface PlannerCellNoteRevision {
  text?: string;
  at?: string;
  by?: string;
}

export interface PlannerCellNote {
  _id?: string;
  text: string;
  authorId?: string;
  authorName?: string;
  createdAt?: string;
  /** Edit/delete audit (Task 8). */
  editedAt?: string;
  editedBy?: string;
  deletedAt?: string;
  deletedBy?: string;
  revisions?: PlannerCellNoteRevision[];
}

export interface PlannerCellDocument {
  documentId: string;
  documentType?: string;
  fileName: string;
  uploadedBy?: string;
  uploadedAt?: string;
}

export interface PlannerAssignment {
  _id: string;
  employeeId: string;
  siteCode: string;
  groupId?: string;
  shiftPatternId?: string;
  startDate: string;
  endDate: string;
  shiftDays: PlannerShiftDay[];
  swingTeam?: string;
  permanentRoomNo?: string;
  travelMode?: 'FIFO' | 'DIDO' | 'LOCAL';
  timesheetCode?: string;
  clientTravelId?: string;
  status: 'draft' | 'published' | 'cancelled';
  /** Who last saved it (username or email), for "updated by" on other screens. */
  lastModifiedBy?: string;
  /** Server's last-write time; the live poll skips rows it already has. */
  updatedAt?: string;
}

/** GET /sites/:siteCode/changes — what other admins changed since `since`. */
export interface RosterChanges {
  serverTime: string;
  assignments: PlannerAssignment[];
  truncated: boolean;
  stamps: { people: string; groups: string; site: string };
}

// Paint saves still on their way to the server, and how many have started. The
// live poll skips a tick while one is out, and drops a result fetched across a
// paint's start: either would merge an older copy over the optimistic paint and
// blink the cell out.
let paintsInFlight = 0;
let paintGeneration = 0;
export const rosterPaintsInFlight = (): number => paintsInFlight;
export const rosterPaintGeneration = (): number => paintGeneration;

// Planby's channel + program shape. Kept local to avoid a transitive type
// import from Planby (which exports types under `planby/dist`).
export interface PlannerChannel {
  uuid: string;
  /** Employee display name — rendered in the sidebar. */
  title: string;
  /** Secondary lines under the name — role / team / room. */
  subtitle?: string;
  team?: string;
  position?: string;
  roomNo?: string;
  /** True when this employee is a deliberate roster member (R1). Non-members
   *  are shown (everyone at the site appears) but badged as not-yet-added. */
  isMember?: boolean;
}

export interface PlannerProgram {
  id: string;
  channelUuid: string;
  title: string;
  since: string; // ISO
  till: string; // ISO
  // Domain payload — kept on the program so the custom renderer can decide
  // exactly how to paint the cell.
  shiftDay: PlannerShiftDay;
  assignmentId: string;
  status: 'draft' | 'published' | 'cancelled';
}

// ============================================================================
// Date helpers — shared so every consumer of "cell day key" agrees on what
// day a given Date represents.
//
// The planner is a calendar (days, not timestamps). Working in UTC breaks
// for east-of-UTC timezones: local midnight in Brisbane (UTC+10) is 14:00
// UTC of the *previous* calendar day. Using `.toISOString().slice(0, 10)`
// for the day-key gives the wrong calendar day for those users — and
// causes the matrix's cell key (computed from local-midnight Dates) to
// disagree with the painted shiftDay.date (sent as UTC midnight),
// producing the "paint doesn't save" symptom.
//
// `ymdKey` here uses LOCAL date components, so a Brisbane admin painting
// 2026-05-18 always gets "2026-05-18" regardless of how the underlying
// Date was constructed. `dayKeyToLocalMidnight` is the inverse — turns
// a "YYYY-MM-DD" string back into the matching local-midnight Date so
// the paint API sees the same timestamp the matrix iteration produced.
//
// Limitation: a single shiftDay.date is stored as one ISO instant. If
// two admins in different timezones view the same roster, they may see
// the cell on different calendar days. For our single-timezone (AU)
// deployment that's not a concern.
// ============================================================================

export function ymdKey(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * The visible day columns, left to right, for a planner window.
 *
 * Shared by the matrix (which renders them) and the board (which needs the same
 * column indexes to place a copy/paste). Two independent copies of this loop
 * would put copy/paste one column out the moment either drifted.
 */
export function enumerateDays(rangeStart: Date, rangeEnd: Date): Date[] {
  const out: Date[] = [];
  const cur = new Date(rangeStart);
  cur.setHours(0, 0, 0, 0);
  const end = new Date(rangeEnd);
  end.setHours(0, 0, 0, 0);
  while (cur.getTime() < end.getTime()) {
    out.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export function dayKeyToLocalMidnight(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  // new Date(year, monthIndex, day) constructs at LOCAL midnight, so
  // toISOString() yields the UTC instant matching the planner's day
  // boundary as the user sees it on screen.
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

function authToken(): string {
  try {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    return userData?.token ?? '';
  } catch {
    return '';
  }
}

/** Man-hours forecast payload (H2) — GET /sites/:siteCode/forecast. */
export interface RosterForecast {
  siteCode: string;
  range: { start: string; end: string };
  totalHours: number;
  /** Work-vs-leave breakdown (read-only, additive). May be absent on responses
   *  from an older backend — treat undefined as 0 / []. */
  leaveHours?: number;
  workHours?: number;
  byLeaveCode?: Array<{ code: string; label: string; hours: number }>;
  headcount: number;
  split: { internalHours: number; externalHours: number };
  byMonth: Array<{ month: string; label: string; hours: number }>;
  byTeam: Array<{ team: string; hours: number; headcount: number }>;
  byEmployee: Array<{
    employeeId: string;
    name: string;
    position: string;
    team: string;
    isExternal: boolean;
    company: string;
    hours: number;
    leaveHours?: number;
    workHours?: number;
    workDays: number;
  }>;
}

/** Admin-only coded man-hours report (Phase 3) — the Job/WC/AC cost-code
 *  breakdown of the roster's admin coding. Read-only; never touches timesheets. */
export interface CodedHoursReport {
  siteCode: string;
  range: { start: string; end: string };
  totalCodedHours: number;
  totalForecastWorkHours: number;
  lineCount: number;
  uncodedWorkDays: number;
  byJob: Array<{ job: string; jobName: string; hours: number }>;
  byCombo: Array<{
    job: string;
    jobName: string;
    workCentre: string;
    workCentreName: string;
    activityCode: string;
    activityCodeDesc: string;
    hours: number;
    lines: number;
  }>;
  byMonth: Array<{ month: string; label: string; hours: number }>;
  byEmployee: Array<{
    employeeId: string;
    name: string;
    codedHours: number;
    forecastHours: number;
    variance: number;
    uncodedDays: number;
  }>;
  warnings: Array<{
    type: 'invalid-combo' | 'coded-vs-forecast';
    message: string;
    [k: string]: unknown;
  }>;
  validation: { checked: number; invalid: number; capped: boolean };
}

/** Read-only per-site access overview (who has admin/editor/viewer access). */
export interface SiteAccessEntry {
  userId?: string;
  email?: string;
  name?: string;
  addedAt?: string;
}
export interface SiteAccess {
  siteCode: string;
  admins: SiteAccessEntry[];
  editors: SiteAccessEntry[];
  viewers: SiteAccessEntry[];
}

/** Per-site day-change approval config (Feature B). */
export interface DayChangeConfig {
  siteCode: string;
  contractMode: 'standard' | 'mode3';
  requiredSetting: boolean | null; // explicit per-site flag; null = use default
  required: boolean; // effective value enforced by the backend
  globalDefault: boolean;
}

/** Employee day-change request (Feature B). */
export interface RosterChangeRequest {
  _id: string;
  employeeId: string;
  employeeName?: string;
  siteCode: string;
  assignmentId: string;
  date: string;
  requested: { workShift?: 'day' | 'night'; leaveCode?: string; notes?: string };
  current: { workShift?: 'day' | 'night'; leaveCode?: string; shiftType?: string };
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  approval?: { actedBy?: string; actedAt?: string; comment?: string };
  createdAt?: string;
}

/** Roster→timesheet population per-site state (Phase 4, GATED). */
export interface TimesheetPopulationState {
  siteCode: string;
  globalEnabled: boolean; // the env master kill-switch
  enabled: boolean; // this site's opt-in
  enabledAt?: string | null;
  enabledBy?: string | null;
  lastRunAt?: string | null;
  lastRunBy?: string | null;
}

/** Dry-run preview result from populateSite. */
export interface TimesheetPopulationPreview {
  siteCode: string;
  enabled: boolean;
  dryRun: boolean;
  ran: boolean;
  summary: { created: number; updated: number; skipped: number; employees: number };
  assignments: Array<{
    employeeId: string;
    employeeName?: string;
    skippedReason?: string;
    leaveDeferred: number;
    summary: { created: number; updated: number; skipped: number; weeksTouched: number };
  }>;
}

/**
 * Result-cap metadata for the two roster list endpoints that cap their output
 * (GET /assignments, GET /sites/:siteCode/historical). Both return a bare JSON
 * array, so the cap is advertised out-of-band on X-Roster-Result-* headers.
 * Read it, so a capped view can say so instead of quietly presenting a partial
 * roster as the whole thing.
 */
export interface RosterResultMeta {
  truncated: boolean;
  /** Cap that was applied. */
  limit: number;
  /** Rows actually returned. */
  count: number;
  /** Rows that matched, when the endpoint counted them. */
  total?: number;
}

/** Null when the backend advertised nothing (older build) — treat as uncapped. */
export function readRosterResultMeta(headers: Headers): RosterResultMeta | null {
  const truncated = headers.get('X-Roster-Result-Truncated');
  if (truncated == null) return null;
  const num = (name: string): number | undefined => {
    const raw = headers.get(name);
    const n = raw == null ? Number.NaN : Number(raw);
    return Number.isFinite(n) ? n : undefined;
  };
  return {
    truncated: truncated === 'true',
    limit: num('X-Roster-Result-Limit') ?? 0,
    count: num('X-Roster-Result-Count') ?? 0,
    total: num('X-Roster-Result-Total'),
  };
}

/** Clash warning returned by POST /assignments/:id/publish. ADVISORY ONLY —
 *  the publish has already succeeded by the time these arrive, and the backend
 *  caps the list at 50. Mirrors RosterConflictWarning in
 *  backend/services/rosterConflictService.ts. */
export type RosterConflictType =
  | 'cross_site_double_booking'
  | 'leave_overlap'
  | 'excessive_consecutive_days';

export interface RosterConflictWarning {
  type: RosterConflictType;
  employeeId: string;
  /** Calendar day the clash starts on ("YYYY-MM-DD"). */
  date: string;
  /** Every affected day, for the multi-day cases. */
  dates: string[];
  /** Server-composed fallback text. Prefer rebuilding from `details` so the
   *  UI can show the employee's name rather than their code. */
  message: string;
  details?: Record<string, unknown>;
}

export interface PublishAssignmentResult {
  assignment?: PlannerAssignment;
  conflicts: RosterConflictWarning[];
}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${authToken()}` },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`GET ${url} → ${res.status} ${text.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

/** As getJson, but also hands back whatever result cap the endpoint advertised. */
async function getJsonWithMeta<T>(
  url: string
): Promise<{ data: T; meta: RosterResultMeta | null }> {
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${authToken()}` },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`GET ${url} → ${res.status} ${text.slice(0, 200)}`);
  }
  const data = (await res.json()) as T;
  return { data, meta: readRosterResultMeta(res.headers) };
}

/** Shared by listAssignments (rows only) and listAssignmentsWithMeta, so the
 *  two can never drift on query shape. */
async function fetchAssignments(
  siteCode: string,
  startDate: Date,
  endDate: Date
): Promise<{ rows: PlannerAssignment[]; meta: RosterResultMeta | null }> {
  const qs = new URLSearchParams({
    siteCode,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  });
  const { data, meta } = await getJsonWithMeta<PlannerAssignment[]>(
    `/api/rosters/assignments?${qs.toString()}`
  );
  return { rows: Array.isArray(data) ? data : [], meta };
}

/**
 * The caller's default roster site, cached for the life of the page.
 *
 * Four screens ask for it — the planner, Forecast, Activity and Historical
 * Data — and it changes only when the user deliberately changes it, through
 * `setDefaultSite`, which refreshes this. Without the cache every tab switch
 * would re-ask for one short string.
 */
let defaultSitePromise: Promise<string> | null = null;

/** Test seam: forget the cached default site. */
export function resetDefaultSiteCache(): void {
  defaultSitePromise = null;
}

// ─── Swing confirmation (docs/features/roster-swing-acceptance-plan.md) ───

/** `dismissed`: a decline a roster admin acknowledged — still a decline, no longer a to-do. */
export type SwingStatus = 'posted' | 'accepted' | 'declined' | 'dismissed' | 'withdrawn';

/** A swing as the planner grid needs it: who, when, and where it stands. */
export interface SiteSwing {
  _id: string;
  employeeId: string;
  /** 'YYYY-MM-DD', inclusive — the same day keys the grid uses. */
  start: string;
  end: string;
  status: SwingStatus;
  respondedAt?: string;
  responseChannel?: 'app' | 'email_link' | 'admin';
}

export interface SwingDay {
  date: string;
  label: string;
  camp?: string;
}

/** One of the signed-in employee's swings waiting for them (or declined). */
export interface MySwing extends SiteSwing {
  siteCode: string;
  siteName: string;
  declineReason?: string;
  postedAt?: string;
  days: SwingDay[];
}

export interface SwingDelivery {
  channel: 'email' | 'push';
  kind: 'offer' | 'reminder' | 'change';
  at: string;
  outcome: 'sent' | 'skipped' | 'failed';
  detail?: string;
}

export interface SwingHistoryEntry {
  at: string;
  action: string;
  by: string;
  channel?: string;
  from?: string;
  to?: string;
  note?: string;
}

/** A swing as the roster admin's Swing Responses tab lists it. */
export interface AdminSwing extends SiteSwing {
  siteCode: string;
  siteName: string;
  employeeName: string;
  periodKey: string;
  postedAt?: string;
  postedBy?: string;
  sentCount?: number;
  respondedBy?: string;
  declineReason?: string;
  adminNote?: string;
  dismissedAt?: string;
  dismissedBy?: string;
  overdue: boolean;
  /** Its start day has passed: its links no longer work, so it cannot be sent again. */
  linkExpired: boolean;
  delivery?: SwingDelivery[];
  history?: SwingHistoryEntry[];
}

export interface SwingAdminSite {
  siteCode: string;
  siteName: string;
  enabled: boolean;
  autoPost: boolean;
  postedPeriods: Array<{ periodKey: string; postedAt: string; postedBy: string; swingCount: number }>;
  declinedCount: number;
  /** Whose public holidays decide which swings are asked about. */
  publicHolidayState: string;
  /** Years with no public holidays for that state — nothing is asked for them. */
  holidayYearsMissing: number[];
}

export interface SwingPeriodPreview {
  periodKey: string;
  toPost: Array<{ employeeId: string; name: string; start: string; end: string; email: string | null }>;
  alreadyPosted: number;
  skipped: Array<{ employeeId: string; name: string; reason: string }>;
}

/** Swing endpoints answer `{ success, data, error }`; surface `error` as the message. */
async function swingRequest<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${authToken()}`,
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success === false) {
    throw new Error(json?.error || `${method} ${url} → ${res.status}`);
  }
  return json.data as T;
}

export const swingApi = {
  siteSwings(siteCode: string, from: string, to: string): Promise<SiteSwing[]> {
    const qs = new URLSearchParams({ from, to });
    return swingRequest('GET', `/api/rosters/sites/${encodeURIComponent(siteCode)}/swings?${qs}`);
  },
  mine(): Promise<{ swings: MySwing[]; disclaimer: string }> {
    return swingRequest('GET', '/api/rosters/swings/mine');
  },
  respond(id: string, action: 'accept' | 'decline', reason?: string): Promise<{ _id: string; status: SwingStatus }> {
    return swingRequest('POST', `/api/rosters/swings/${encodeURIComponent(id)}/respond`, { action, reason });
  },
  adminSites(): Promise<{ featureOn: boolean; sites: SwingAdminSite[] }> {
    return swingRequest('GET', '/api/rosters/swing-admin/sites');
  },
  setSiteConfig(siteCode: string, patch: { enabled?: boolean; autoPost?: boolean; publicHolidayState?: string }): Promise<unknown> {
    return swingRequest('PUT', `/api/rosters/swing-admin/sites/${encodeURIComponent(siteCode)}/config`, patch);
  },
  previewPeriod(siteCode: string, periodKey: string): Promise<SwingPeriodPreview> {
    return swingRequest('GET', `/api/rosters/swing-admin/sites/${encodeURIComponent(siteCode)}/periods/${periodKey}/preview`);
  },
  postPeriod(siteCode: string, periodKey: string): Promise<SwingPeriodPreview> {
    return swingRequest('POST', `/api/rosters/swing-admin/sites/${encodeURIComponent(siteCode)}/periods/${periodKey}/post`);
  },
  adminSwings(filters: { siteCode?: string; status?: string; periodKey?: string }): Promise<AdminSwing[]> {
    const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => !!v) as [string, string][]);
    return swingRequest('GET', `/api/rosters/swing-admin/swings?${qs}`);
  },
  resend(id: string, note?: string): Promise<AdminSwing> {
    return swingRequest('POST', `/api/rosters/swing-admin/swings/${encodeURIComponent(id)}/resend`, { note });
  },
  acceptOnBehalf(id: string, note: string): Promise<AdminSwing> {
    return swingRequest('POST', `/api/rosters/swing-admin/swings/${encodeURIComponent(id)}/accept`, { note });
  },
  dismiss(id: string, note?: string): Promise<AdminSwing> {
    return swingRequest('POST', `/api/rosters/swing-admin/swings/${encodeURIComponent(id)}/dismiss`, { note });
  },
};

export const plannerApi = {
  listSites(): Promise<PlannerSite[]> {
    return getJson<PlannerSite[]>('/api/rosters/sites');
  },
  /**
   * The site this user chose to open on, or '' when they have not chosen one.
   *
   * Never rejects. A preference that cannot be read must not stop a roster
   * loading — the caller just falls through to the next candidate. See
   * `components/rosters/siteSelection` for the order they fall through in.
   */
  getDefaultSite(): Promise<string> {
    if (!defaultSitePromise) {
      defaultSitePromise = getJson<{ siteCode?: unknown }>('/api/users/me/roster-default-site')
        .then(body => (typeof body?.siteCode === 'string' ? body.siteCode : ''))
        .catch(() => '');
    }
    return defaultSitePromise;
  },
  /**
   * This user's favourite paints for one site, in the order they arranged
   * them. Never rejects: a planner that cannot load a preference still paints.
   * See ./paintFavourites for what the keys mean.
   */
  getPaintFavourites(siteCode: string): Promise<string[]> {
    return getJson<{ paints?: unknown }>(
      `/api/users/me/roster-paint-favourites?siteCode=${encodeURIComponent(siteCode)}`
    )
      .then(body => (Array.isArray(body?.paints) ? (body.paints as string[]) : []))
      .catch(() => []);
  },
  /** Replace this user's favourites for one site. Throws, so a failed save is
   *  said out loud rather than reverting silently on the next load. */
  async setPaintFavourites(siteCode: string, paints: string[]): Promise<string[]> {
    const res = await fetch('/api/users/me/roster-paint-favourites', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
      body: JSON.stringify({ siteCode, paints }),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`PUT paint favourites → ${res.status} ${t.slice(0, 200)}`);
    }
    const body = await res.json().catch(() => ({}) as { paints?: unknown });
    return Array.isArray(body?.paints) ? (body.paints as string[]) : paints;
  },
  /** Set, or with an empty code clear, this user's default site. Throws, so the
   *  caller can say it did not save rather than showing a silent no-op. */
  async setDefaultSite(siteCode: string): Promise<string> {
    const res = await fetch('/api/users/me/roster-default-site', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
      body: JSON.stringify({ siteCode }),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`PUT default site → ${res.status} ${t.slice(0, 200)}`);
    }
    const body = await res.json().catch(() => ({}) as { siteCode?: unknown });
    const saved = typeof body?.siteCode === 'string' ? body.siteCode : siteCode;
    defaultSitePromise = Promise.resolve(saved);
    return saved;
  },
  listPatterns(): Promise<PlannerPattern[]> {
    return getJson<PlannerPattern[]>('/api/rosters/patterns');
  },
  /** Set an employee's Team (level-1 child group) membership for a site (#4). */
  async setEmployeeTeam(
    siteCode: string,
    employeeId: string,
    groupId: string | null
  ): Promise<{ groupId: string | null; groupName: string | null }> {
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/employees/${encodeURIComponent(employeeId)}/team`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify({ groupId }),
      }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`PUT team → ${res.status} ${t.slice(0, 200)}`);
    }
    return res.json();
  },
  /** Set an employee's parent Group (level-0 group) membership for a site. */
  async setEmployeeGroup(
    siteCode: string,
    employeeId: string,
    groupId: string | null
  ): Promise<{ groupId: string | null; groupName: string | null }> {
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/employees/${encodeURIComponent(employeeId)}/group`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify({ groupId }),
      }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`PUT group → ${res.status} ${t.slice(0, 200)}`);
    }
    return res.json();
  },
  listGroups(siteCode: string): Promise<PlannerGroup[]> {
    return getJson<PlannerGroup[]>(`/api/rosters/groups?siteCode=${encodeURIComponent(siteCode)}`);
  },
  /**
   * Persist the admin's preferred group order for a site. `orderedIds` is the
   * group ids top-to-bottom; the server stores each group's index as its
   * displayOrder.
   */
  /**
   * Persist the manual order of people WITHIN one group. Anyone not listed is
   * left untouched and keeps sorting after the ordered ones.
   */
  async reorderGroupMembers(groupId: string, orderedEmployeeIds: string[]): Promise<void> {
    const res = await fetch(
      `/api/rosters/groups/${encodeURIComponent(groupId)}/members/order`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify({ orderedEmployeeIds }),
      }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`PUT member order → ${res.status} ${t.slice(0, 200)}`);
    }
  },
  async reorderGroups(siteCode: string, orderedIds: string[]): Promise<void> {
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/groups/order`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify({ orderedIds }),
      }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`PUT group order → ${res.status} ${t.slice(0, 200)}`);
    }
  },
  async listAssignments(
    siteCode: string,
    startDate: Date,
    endDate: Date
  ): Promise<PlannerAssignment[]> {
    const { rows } = await fetchAssignments(siteCode, startDate, endDate);
    return rows;
  },
  /** Same call, but keeps the result-cap metadata so the board can tell the
   *  admin when they're looking at a partial roster. */
  listAssignmentsWithMeta(
    siteCode: string,
    startDate: Date,
    endDate: Date
  ): Promise<{ rows: PlannerAssignment[]; meta: RosterResultMeta | null }> {
    return fetchAssignments(siteCode, startDate, endDate);
  },
  /**
   * Publish one assignment — the worker can see it from this point, and the
   * site's notification channels fire.
   *
   * `conflicts` is advisory and arrives AFTER the fact: the backend never
   * blocks a publish on them. An empty array means "nothing flagged", not
   * "not checked".
   */
  async publishAssignment(assignmentId: string): Promise<PublishAssignmentResult> {
    const res = await fetch(
      `/api/rosters/assignments/${encodeURIComponent(assignmentId)}/publish`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
      }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`POST publish → ${res.status} ${t.slice(0, 200)}`);
    }
    const body = (await res.json().catch(() => ({}))) as {
      assignment?: PlannerAssignment;
      conflicts?: RosterConflictWarning[];
    };
    return {
      assignment: body?.assignment,
      conflicts: Array.isArray(body?.conflicts) ? body.conflicts : [],
    };
  },
  /**
   * Pull a published assignment back to draft — the recovery path for a roster
   * published too early or for the wrong period. The painted cells are
   * untouched; only visibility to the worker is withdrawn. 400s if the
   * assignment is not currently published.
   */
  /**
   * Clash warnings for an assignment WITHOUT publishing it. Read-only; the
   * same checks the publish response carries, run before the point of no
   * return (publishing is also what emails and pushes the worker).
   */
  async checkAssignmentConflicts(assignmentId: string): Promise<{
    conflicts: RosterConflictWarning[];
    capped: boolean;
  }> {
    const res = await fetch(
      `/api/rosters/assignments/${encodeURIComponent(assignmentId)}/conflicts`,
      { headers: { Authorization: `Bearer ${authToken()}` } }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`GET conflicts → ${res.status} ${t.slice(0, 200)}`);
    }
    const body = (await res.json().catch(() => ({}))) as {
      conflicts?: RosterConflictWarning[];
      capped?: boolean;
    };
    return { conflicts: body.conflicts ?? [], capped: !!body.capped };
  },

  /** What changed on a site since `since` (null = just the baseline stamps). */
  async rosterChanges(
    siteCode: string,
    since: string | null,
    startDate: Date,
    endDate: Date
  ): Promise<RosterChanges> {
    const qs = new URLSearchParams({ startDate: startDate.toISOString(), endDate: endDate.toISOString() });
    if (since) qs.set('since', since);
    const res = await fetch(`/api/rosters/sites/${encodeURIComponent(siteCode)}/changes?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${authToken()}` },
    });
    if (!res.ok) throw new Error(`GET changes → ${res.status}`);
    return (await res.json()) as RosterChanges;
  },

  /**
   * Heartbeat "I am looking at this site's planner", and get back everyone
   * else who is. Advisory: it locks nothing, and a failure resolves to an
   * empty list rather than throwing — a presence banner must never be able to
   * break the board it sits on.
   */
  async rosterPresence(siteCode: string): Promise<RosterPresencePeer[]> {
    try {
      const res = await fetch(
        `/api/rosters/sites/${encodeURIComponent(siteCode)}/presence`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        }
      );
      if (!res.ok) return [];
      const body = (await res.json().catch(() => ({}))) as { others?: RosterPresencePeer[] };
      return body.others ?? [];
    } catch {
      return [];
    }
  },

  async unpublishAssignment(assignmentId: string): Promise<PlannerAssignment | undefined> {
    const res = await fetch(
      `/api/rosters/assignments/${encodeURIComponent(assignmentId)}/unpublish`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
      }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`POST unpublish → ${res.status} ${t.slice(0, 200)}`);
    }
    const body = (await res.json().catch(() => ({}))) as { assignment?: PlannerAssignment };
    return body?.assignment;
  },
  searchEmployees(query: string): Promise<PlannerEmployee[]> {
    return getJson<PlannerEmployee[]>(
      `/api/rosters/employees/search?q=${encodeURIComponent(query)}`
    );
  },
  /**
   * Patch a single employee's RosterEmployeeProfile at a site. Used by
   * inline editors in the matrix (e.g. the RST dropdown). Pass `null`
   * for a field to clear it; omit a field to leave it untouched.
   */
  async updateEmployeeProfile(
    siteCode: string,
    employeeId: string,
    // null clears a field. A name is accepted for an external person only
    // (the server enforces it).
    patch: { [K in Exclude<keyof PlannerEmployeeProfile, 'customFields'>]?: PlannerEmployeeProfile[K] | null } & {
      customFields?: Record<string, string | null>;
      firstName?: string;
      lastName?: string;
    }
  ): Promise<unknown> {
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/employees/${encodeURIComponent(employeeId)}/profile`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify(patch),
      }
    );
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`PUT profile → ${res.status} ${text.slice(0, 200)}`);
    }
    return res.json();
  },
  /**
   * Every HRPerson assigned to this site — used to populate the planner
   * with EVERY worker (rostered or not), so admins can paint cells onto
   * blank rows without first running the "Add to roster" modal.
   */
  /**
   * Full site directory — every site in SiteMapping joined with
   * RosterSite for claim info. Mirrors the list the Training Competency
   * Dashboard shows so the roster picker has the same set.
   */
  listSiteDirectory(): Promise<PlannerSiteDirectoryEntry[]> {
    return getJson<PlannerSiteDirectoryEntry[]>('/api/rosters/site-directory');
  },
  /**
   * Bulk-import per-employee Excel-only fields from a parsed CSV/XLSX.
   * Frontend parses the file client-side and POSTs the normalised rows;
   * server matches by SURNAME + FIRST NAME and reports per-row outcomes.
   */
  async importEmployeeProfiles(
    siteCode: string,
    rows: ProfileImportRow[],
    sourceFile?: string
  ): Promise<ProfileImportResult> {
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/employee-profiles/import`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify({ rows, sourceFile: sourceFile ?? null }),
      }
    );
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`POST employee-profile import → ${res.status} ${text.slice(0, 200)}`);
    }
    return (await res.json()) as ProfileImportResult;
  },
  listSiteEmployees(siteCode: string): Promise<PlannerEmployee[]> {
    return getJson<PlannerEmployee[]>(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/employees`
    );
  },
  /**
   * Earliest start / latest end across the site's assignments, so the planner
   * can open its window on the site's actual data instead of always "today".
   * Returns nulls when the site has no roster data.
   */
  getSiteDataRange(
    siteCode: string
  ): Promise<{ start: string | null; end: string | null }> {
    return getJson<{ start: string | null; end: string | null }>(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/data-range`
    );
  },
  /**
   * Bulk paint cells. The server lazy-creates RosterAssignments and
   * shiftDays as needed, so the planner doesn't need to know whether a
   * target employee already has an assignment. windowStart/windowEnd
   * (optional) tell the server how wide to make any new scaffold
   * assignments — pass the planner's visible date range so a multi-day
   * paint on one employee produces ONE assignment with multiple
   * shiftDays instead of many one-day scaffolds.
   *
   * Returns per-update success + the resulting assignmentId so the
   * planner can splice the new doc into local state without a full
   * reload.
   */
  async paintCells(
    siteCode: string,
    updates: PaintUpdate[],
    windowStart?: Date,
    windowEnd?: Date
  ): Promise<{ results: PaintResult[]; assignments: PlannerAssignment[] }> {
    const body: Record<string, unknown> = { updates };
    if (windowStart && windowEnd) {
      body.windowStart = windowStart.toISOString();
      body.windowEnd = windowEnd.toISOString();
    }
    paintsInFlight++;
    paintGeneration++;
    let res: Response;
    try {
      res = await fetch(`/api/rosters/sites/${encodeURIComponent(siteCode)}/paint`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify(body),
      });
    } finally {
      paintsInFlight--;
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`POST paint ${siteCode} → ${res.status} ${text.slice(0, 200)}`);
    }
    // `assignments` = every assignment the paint touched, as stored — the
    // planner swaps these in rather than reloading the whole roster.
    const out = (await res.json()) as { results: PaintResult[]; assignments?: PlannerAssignment[] };
    return { results: out.results, assignments: out.assignments ?? [] };
  },
  /**
   * Append a note to a cell's audit trail (Task 7). Keyed by employeeId +
   * date so it works for any cell, lazy-creating the day if needed. Returns
   * the updated note history.
   */
  async addCellNote(
    siteCode: string,
    employeeId: string,
    date: string,
    text: string,
    assignmentId?: string
  ): Promise<{
    assignmentId: string;
    dayIndex: number;
    notes: string;
    noteHistory: PlannerCellNote[];
  }> {
    const res = await fetch(`/api/rosters/sites/${encodeURIComponent(siteCode)}/days/notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
      body: JSON.stringify({ employeeId, date, text, assignmentId }),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`POST cell note → ${res.status} ${t.slice(0, 200)}`);
    }
    return res.json();
  },
  /**
   * Edit a cell note's text (Task 8). Prior text is preserved server-side in
   * the note's revisions[]. Allowed for the author or a roster admin.
   */
  async editCellNote(
    siteCode: string,
    employeeId: string,
    date: string,
    noteId: string,
    text: string,
    assignmentId?: string
  ): Promise<{
    assignmentId: string;
    dayIndex: number;
    notes: string;
    noteHistory: PlannerCellNote[];
  }> {
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/days/notes/${encodeURIComponent(noteId)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify({ employeeId, date, text, assignmentId }),
      }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`PUT cell note → ${res.status} ${t.slice(0, 200)}`);
    }
    return res.json();
  },
  /**
   * Soft-delete a cell note (Task 8) — tombstoned server-side, audit kept.
   * Allowed for the author or a roster admin.
   */
  async deleteCellNote(
    siteCode: string,
    employeeId: string,
    date: string,
    noteId: string,
    assignmentId?: string
  ): Promise<{
    assignmentId: string;
    dayIndex: number;
    notes: string;
    noteHistory: PlannerCellNote[];
  }> {
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/days/notes/${encodeURIComponent(noteId)}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify({ employeeId, date, assignmentId }),
      }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`DELETE cell note → ${res.status} ${t.slice(0, 200)}`);
    }
    return res.json();
  },
  /**
   * Add an employee to the site's roster as a MEMBER, without a shift pattern
   * or any cells (R1). Applying a pattern afterwards is optional.
   */
  /**
   * The worker's own roster notifications. Publish and post-publish edits have
   * always written these rows with an 'in_app' channel, but nothing in the app
   * ever read them — My Shifts is where they surface.
   */
  async fetchMyNotifications(status = 'sent'): Promise<RosterNotificationRow[]> {
    const res = await fetch(
      `/api/rosters/notifications?status=${encodeURIComponent(status)}&limit=20`,
      { headers: { Authorization: `Bearer ${authToken()}` } }
    );
    if (!res.ok) throw new Error(`GET /notifications → ${res.status}`);
    return res.json();
  },

  async markNotificationRead(id: string): Promise<void> {
    const res = await fetch(`/api/rosters/notifications/${encodeURIComponent(id)}/read`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
    });
    if (!res.ok) throw new Error(`PUT /notifications/${id}/read → ${res.status}`);
  },

  async addEmployeeToRoster(
    siteCode: string,
    employeeId: string
  ): Promise<{ ok: boolean; member: boolean }> {
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/employees/${encodeURIComponent(
        employeeId
      )}/add`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
      }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`Add to roster → ${res.status} ${t.slice(0, 200)}`);
    }
    return res.json();
  },
  /**
   * Non-destructively set a person's Active/Inactive state and/or roster-type
   * label for a site. Inactive hides them from the day-to-day board but keeps
   * their assignments (and forecast hours) — NOT the same as removing them.
   */
  async setRosterMeta(
    siteCode: string,
    employeeId: string,
    patch: { active?: boolean; rosterType?: string }
  ): Promise<{ ok: boolean; rosterActive: boolean; rosterType?: string }> {
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/employees/${encodeURIComponent(
        employeeId
      )}/roster-meta`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify(patch),
      }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`PUT roster-meta → ${res.status} ${t.slice(0, 200)}`);
    }
    return res.json();
  },
  /** Delete a contractor (external person) from the site registry (C1). */
  async deleteExternalPerson(siteCode: string, employeeId: string): Promise<{ ok: boolean }> {
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/external-people/${encodeURIComponent(
        employeeId
      )}`,
      { method: 'DELETE', headers: { Authorization: `Bearer ${authToken()}` } }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`Delete contractor → ${res.status} ${t.slice(0, 200)}`);
    }
    return res.json();
  },
  /**
   * Remove an employee from a site's roster (Task 6) from today: their days
   * from today are cancelled, earlier days stay as history. `removed` counts
   * the assignments cancelled or cut short; `message` comes only when they
   * were not on the roster, and nothing changed.
   */
  async removeEmployeeFromRoster(
    siteCode: string,
    employeeId: string
  ): Promise<{ ok: boolean; removed: number; message?: string }> {
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/employees/${encodeURIComponent(
        employeeId
      )}/remove`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
      }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`Remove from roster → ${res.status} ${t.slice(0, 200)}`);
    }
    return res.json();
  },
  /**
   * Export a site's roster as a matrix CSV (Task 5/16) over a date range.
   * Returns the CSV bytes as a Blob (auth header required).
   */
  /** Per-site roster notification config (N1). */
  getNotificationConfig(
    siteCode: string
  ): Promise<{ publishEmail?: boolean; publishPush?: boolean }> {
    return getJson(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/notification-config`
    );
  },
  async updateNotificationConfig(
    siteCode: string,
    patch: { publishEmail?: boolean; publishPush?: boolean }
  ): Promise<{ publishEmail?: boolean; publishPush?: boolean }> {
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/notification-config`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify(patch),
      }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`PUT notification-config → ${res.status} ${t.slice(0, 200)}`);
    }
    return res.json();
  },
  /** Read-only site access overview (who can access this site). */
  getSiteAccess(siteCode: string): Promise<SiteAccess> {
    return getJson<SiteAccess>(`/api/rosters/sites/${encodeURIComponent(siteCode)}/access`);
  },
  /** Per-site day-change approval config (Feature B). */
  getDayChangeConfig(siteCode: string): Promise<DayChangeConfig> {
    return getJson<DayChangeConfig>(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/day-change-config`
    );
  },
  async setDayChangeConfig(
    siteCode: string,
    patch: { required?: boolean; contractMode?: 'standard' | 'mode3' }
  ): Promise<{ siteCode: string; contractMode: string; required: boolean }> {
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/day-change-config`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify(patch),
      }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`PUT day-change-config → ${res.status} ${t.slice(0, 160)}`);
    }
    return res.json();
  },
  /** Employee day-change request → approval (Feature B). */
  async submitChangeRequest(body: {
    assignmentId: string;
    date: string;
    requested: { workShift?: 'day' | 'night'; leaveCode?: string; notes?: string };
  }): Promise<RosterChangeRequest> {
    const res = await fetch('/api/rosters/change-requests', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      let msg = t;
      try {
        msg = JSON.parse(t).error || t;
      } catch {
        /* keep raw */
      }
      throw new Error(msg.slice(0, 200) || `POST change-request → ${res.status}`);
    }
    return res.json();
  },
  async cancelChangeRequest(id: string): Promise<RosterChangeRequest> {
    const res = await fetch(
      `/api/rosters/change-requests/${encodeURIComponent(id)}/cancel`,
      { method: 'POST', headers: { Authorization: `Bearer ${authToken()}` } }
    );
    if (!res.ok) throw new Error(`Cancel → ${res.status}`);
    return res.json();
  },
  listChangeRequests(status = 'pending', siteCode?: string): Promise<RosterChangeRequest[]> {
    const qs = new URLSearchParams({ status });
    if (siteCode) qs.set('siteCode', siteCode);
    return getJson<RosterChangeRequest[]>(`/api/rosters/change-requests?${qs.toString()}`);
  },
  async approveChangeRequest(id: string, comment?: string): Promise<{ success: boolean }> {
    const res = await fetch(
      `/api/rosters/change-requests/${encodeURIComponent(id)}/approve`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify({ comment }),
      }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`Approve → ${res.status} ${t.slice(0, 160)}`);
    }
    return res.json();
  },
  async rejectChangeRequest(id: string, comment: string): Promise<{ success: boolean }> {
    const res = await fetch(
      `/api/rosters/change-requests/${encodeURIComponent(id)}/reject`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify({ comment }),
      }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      let msg = t;
      try {
        msg = JSON.parse(t).error || t;
      } catch {
        /* keep raw */
      }
      throw new Error(msg.slice(0, 200) || `Reject → ${res.status}`);
    }
    return res.json();
  },
  /** Roster→timesheet population (Phase 4, GATED) — per-site state. */
  getTimesheetPopulation(siteCode: string): Promise<TimesheetPopulationState> {
    return getJson<TimesheetPopulationState>(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/timesheet-population`
    );
  },
  async setTimesheetPopulation(
    siteCode: string,
    enabled: boolean
  ): Promise<TimesheetPopulationState> {
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/timesheet-population`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify({ enabled }),
      }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`PUT timesheet-population → ${res.status} ${t.slice(0, 200)}`);
    }
    return res.json();
  },
  /** Dry-run preview of what population would create/update/skip. Writes nothing. */
  previewTimesheetPopulation(
    siteCode: string
  ): Promise<TimesheetPopulationPreview> {
    return getJson<TimesheetPopulationPreview>(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/timesheet-population/preview`
    );
  },
  /** Man-hours forecast (H2) rolled up per employee/team/month over a range. */
  getForecast(
    siteCode: string,
    startDate: string,
    endDate: string
  ): Promise<RosterForecast> {
    const qs = new URLSearchParams({ startDate, endDate }).toString();
    return getJson<RosterForecast>(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/forecast?${qs}`
    );
  },
  /**
   * Matrix CSV for a site over a date range.
   *
   * `include` names the OPT-IN extras ('hours' | 'camp' | 'room'). Passing
   * none reproduces the original file byte for byte — these go to client
   * administrators and may be read by something downstream, so the default
   * shape is not ours to widen.
   */
  async exportRosterCsv(
    siteCode: string,
    startDate: string,
    endDate: string,
    include: string[] = []
  ): Promise<Blob> {
    const params: Record<string, string> = { startDate, endDate };
    if (include.length > 0) params.include = include.join(',');
    const qs = new URLSearchParams(params).toString();
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/export?${qs}`,
      { headers: { Authorization: `Bearer ${authToken()}` } }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`Export → ${res.status} ${t.slice(0, 200)}`);
    }
    return res.blob();
  },
  /** Admin-only coded man-hours report (Phase 3). */
  getCodedHours(
    siteCode: string,
    startDate: string,
    endDate: string
  ): Promise<CodedHoursReport> {
    const qs = new URLSearchParams({ startDate, endDate }).toString();
    return getJson<CodedHoursReport>(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/coded-hours?${qs}`
    );
  },
  async exportCodedHoursCsv(siteCode: string, startDate: string, endDate: string): Promise<Blob> {
    const qs = new URLSearchParams({ startDate, endDate, format: 'csv' }).toString();
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/coded-hours?${qs}`,
      { headers: { Authorization: `Bearer ${authToken()}` } }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`Export → ${res.status} ${t.slice(0, 200)}`);
    }
    return res.blob();
  },
  /**
   * Upload a file (png/jpg/pdf) onto a cell (Task 9). Lazy-creates the day.
   * Returns the updated document list for that cell.
   */
  async uploadCellDocument(
    siteCode: string,
    employeeId: string,
    date: string,
    file: File,
    documentType?: string,
    assignmentId?: string
  ): Promise<{ assignmentId: string; dayIndex: number; documents: PlannerCellDocument[] }> {
    const form = new FormData();
    form.append('file', file);
    form.append('employeeId', employeeId);
    form.append('date', date);
    if (documentType) form.append('documentType', documentType);
    if (assignmentId) form.append('assignmentId', assignmentId);
    const res = await fetch(`/api/rosters/sites/${encodeURIComponent(siteCode)}/days/documents`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken()}` }, // no Content-Type — browser sets the multipart boundary
      body: form,
    });
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`Upload → ${res.status} ${t.slice(0, 200)}`);
    }
    return res.json();
  },
  async deleteCellDocument(docId: string): Promise<void> {
    const res = await fetch(`/api/rosters/documents/${encodeURIComponent(docId)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${authToken()}` },
    });
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`Delete → ${res.status} ${t.slice(0, 200)}`);
    }
  },
  /** Fetch a document's bytes (auth header required) as a Blob for view/download. */
  async rosterDocumentBlob(docId: string): Promise<Blob> {
    const res = await fetch(`/api/rosters/documents/${encodeURIComponent(docId)}`, {
      headers: { Authorization: `Bearer ${authToken()}` },
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      // Both parts: a refusal's `message` is what tells the person who to ask.
      const reason = [body.error, body.message].filter(Boolean).join(' - ');
      throw new Error(reason || `The attachment could not be opened (${res.status}).`);
    }
    return res.blob();
  },
  async createAssignment(payload: Record<string, unknown>): Promise<PlannerAssignment> {
    const res = await fetch('/api/rosters/assignments', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken()}`,
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`POST /api/rosters/assignments → ${res.status} ${text.slice(0, 200)}`);
    }
    return (await res.json()) as PlannerAssignment;
  },
  listUnclaimedSites(): Promise<PlannerSite[]> {
    return getJson<PlannerSite[]>('/api/rosters/sites/unclaimed');
  },
  async claimSite(siteCode: string): Promise<void> {
    const res = await fetch(`/api/rosters/sites/${encodeURIComponent(siteCode)}/claim`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken()}` },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`POST claim ${siteCode} → ${res.status} ${text.slice(0, 200)}`);
    }
  },
  async unclaimSite(siteCode: string): Promise<void> {
    const res = await fetch(`/api/rosters/sites/${encodeURIComponent(siteCode)}/unclaim`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken()}` },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`POST unclaim ${siteCode} → ${res.status} ${text.slice(0, 200)}`);
    }
  },
  listCamps(siteCode: string): Promise<PlannerCamp[]> {
    return getJson<PlannerCamp[]>(`/api/rosters/sites/${encodeURIComponent(siteCode)}/camps`);
  },
  /**
   * Codes painted on this site's cells that no palette can reproduce — the
   * imported Excel abbreviations nobody has classified yet. Advisory and
   * read-only; the admin decides what each one means.
   */
  listUncoveredCodes(
    siteCode: string,
    startDate: Date,
    endDate: Date
  ): Promise<UncoveredCode[]> {
    const qs = new URLSearchParams({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });
    return getJson<UncoveredCode[]>(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/code-coverage?${qs}`
    );
  },
  /** List a site's admin-defined custom matrix columns (Task 8). */
  listSiteColumns(siteCode: string): Promise<PlannerCustomColumn[]> {
    return getJson<PlannerCustomColumn[]>(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/columns`
    );
  },
  /** Replace a site's custom column definitions (admin). Returns the saved set. */
  async saveSiteColumns(
    siteCode: string,
    columns: PlannerCustomColumn[]
  ): Promise<PlannerCustomColumn[]> {
    const res = await fetch(`/api/rosters/sites/${encodeURIComponent(siteCode)}/columns`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
      body: JSON.stringify({ columns }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`PUT columns ${siteCode} → ${res.status} ${text.slice(0, 200)}`);
    }
    return (await res.json()) as PlannerCustomColumn[];
  },
  /** List a site's admin-defined custom cell codes/colours (feedback #4/#5). */
  listSiteCodes(siteCode: string): Promise<PlannerCustomCode[]> {
    return getJson<PlannerCustomCode[]>(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/codes`
    );
  },
  /** Replace a site's custom cell codes (admin). Returns the saved set. */
  async saveSiteCodes(
    siteCode: string,
    codes: PlannerCustomCode[]
  ): Promise<PlannerCustomCode[]> {
    const res = await fetch(`/api/rosters/sites/${encodeURIComponent(siteCode)}/codes`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
      body: JSON.stringify({ codes }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`PUT codes ${siteCode} → ${res.status} ${text.slice(0, 200)}`);
    }
    return (await res.json()) as PlannerCustomCode[];
  },
  /** Add a non-GreenTree person (subcontractor) to a site — creates an EXT- row. */
  async addExternalPerson(
    siteCode: string,
    payload: {
      firstName: string;
      lastName: string;
      company?: string;
      role?: string;
      email?: string;
      phone?: string;
      contactNotes?: string;
      travelMode?: string;
    }
  ): Promise<PlannerEmployee> {
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/external-people`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify(payload),
      }
    );
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`POST external-people ${siteCode} → ${res.status} ${text.slice(0, 200)}`);
    }
    return (await res.json()) as PlannerEmployee;
  },
  async createCamp(siteCode: string, payload: Record<string, unknown>): Promise<PlannerCamp> {
    const res = await fetch(`/api/rosters/sites/${encodeURIComponent(siteCode)}/camps`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`POST camp ${siteCode} → ${res.status} ${text.slice(0, 200)}`);
    }
    return (await res.json()) as PlannerCamp;
  },
  async updateCamp(
    siteCode: string,
    campId: string,
    payload: Record<string, unknown>
  ): Promise<PlannerCamp> {
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/camps/${encodeURIComponent(campId)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify(payload),
      }
    );
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`PUT camp ${campId} → ${res.status} ${text.slice(0, 200)}`);
    }
    return (await res.json()) as PlannerCamp;
  },
  async deleteCamp(siteCode: string, campId: string): Promise<void> {
    const res = await fetch(
      `/api/rosters/sites/${encodeURIComponent(siteCode)}/camps/${encodeURIComponent(campId)}`,
      { method: 'DELETE', headers: { Authorization: `Bearer ${authToken()}` } }
    );
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`DELETE camp ${campId} → ${res.status} ${text.slice(0, 200)}`);
    }
  },
};

/**
 * Compute per-day shift cells from a rotation pattern, a date range, and an
 * optional swing-team membership. The swing team determines the offset
 * into the cycle (the planner's central FIFO primitive).
 *
 * For a 14-on/14-off pattern with swingVocab = ['A','C'], team A starts at
 * offset 0 (day 1 = first day of work) and team C starts at offset 14
 * (day 1 = first day off, work begins on day 15). Custom offsetDays can
 * override the derived value for mid-cycle joins.
 */
export function materializeShiftDays(
  pattern: PlannerPattern,
  startDate: Date,
  endDate: Date,
  swingTeam?: string,
  offsetOverride?: number
): PlannerShiftDay[] {
  if (!pattern.segments || pattern.segments.length === 0) return [];
  const cycle = pattern.cycleLength || pattern.rotationDays;
  if (!cycle) return [];

  // Derive offset from the swing team's position in swingVocab. Teams are
  // equally spaced around the cycle: team N gets offset = N * cycle/teams.
  let offset = 0;
  if (offsetOverride !== undefined) {
    offset = offsetOverride;
  } else if (swingTeam && pattern.swingVocab && pattern.swingVocab.length > 0) {
    const idx = pattern.swingVocab.indexOf(swingTeam);
    if (idx >= 0) {
      offset = Math.round((idx * cycle) / pattern.swingVocab.length);
    }
  }

  // Pre-compute the day → segment lookup table for one cycle so the inner
  // loop is O(1) instead of O(segments) per day.
  const segmentByDay: Array<PlannerPattern['segments'][number] | undefined> = new Array(cycle);
  let cursor = 0;
  for (const seg of pattern.segments) {
    for (let i = 0; i < seg.days && cursor < cycle; i++) {
      segmentByDay[cursor++] = seg;
    }
  }

  const days: PlannerShiftDay[] = [];
  const cur = new Date(startDate);
  cur.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  let dayCounter = 0;
  while (cur.getTime() <= end.getTime()) {
    const cyclePos = (((dayCounter + offset) % cycle) + cycle) % cycle;
    const seg = segmentByDay[cyclePos];
    if (seg) {
      days.push({
        date: new Date(cur).toISOString(),
        segmentId: seg.segmentId,
        segmentName: seg.segmentName,
        shiftType: seg.shiftType,
        coverageRequired: seg.shiftType === 'travel',
        // Applying a pattern REPLACES the days it covers (user decision
        // 2026-10-29), so every field the segment does not define is sent as
        // an explicit NULL: the server's merge reads null as "clear", while
        // `undefined` is dropped by JSON and keeps whatever was there. That is
        // how a travel day used to keep a full shift's forecast hours, and how
        // a stale FI/OFF/OS code (drawn over the segment) or last rotation's
        // camp survived a re-apply. Callers re-stamp the chosen camp.
        color: seg.color ?? null,
        abbreviation: seg.abbreviation ?? null,
        hoursPerDay: seg.hoursPerDay ?? null,
        // Day/night and Job/WC/AC coding are work-day concepts.
        workShift: (seg.shiftType === 'work' ? seg.workShift : null) ?? null,
        timesheetCoding:
          seg.shiftType === 'work' && seg.defaultCoding?.length ? seg.defaultCoding : null,
        leaveCode: null,
        campCode: null,
        needsReview: false,
        reviewNote: null,
        // Not replaced: a day holding real leave (AL, SL, ...), which the
        // server leaves exactly as it is because leave is changed by hand;
        // and notes, note history and attachments, absent here so they
        // survive — they are the audit trail and the files (flight tickets)
        // a re-roster must not destroy.
      });
    }
    cur.setDate(cur.getDate() + 1);
    dayCounter++;
  }
  return days;
}

/**
 * What applying a pattern does to the days already on the roster, for the
 * warning shown before it runs: how many it replaces, and how many it keeps
 * because they hold real leave. Counted once per person and day, on the days
 * the pattern covers.
 */
export function patternApplyImpact(
  existing: PlannerAssignment[],
  days: PlannerShiftDay[]
): { replaced: number; leaveKept: number } {
  const covered = new Set(days.map(d => ymdKey(d.date)));
  const replaced = new Set<string>();
  const leaveKept = new Set<string>();
  for (const a of existing) {
    for (const d of a.shiftDays ?? []) {
      const day = ymdKey(d.date);
      if (!covered.has(day)) continue;
      const key = `${a.employeeId}::${day}`;
      // Real leave as the server reads it (holdsRealLeave): a leave code in
      // leaveCode or, on older cells, in the abbreviation.
      const code = (d.leaveCode || d.abbreviation || '').trim().toUpperCase();
      if (LEGEND[code as LeaveCode]?.isLeave) leaveKept.add(key);
      else if (d.leaveCode || d.abbreviation || d.segmentId || d.shiftType || d.campCode) replaced.add(key);
    }
  }
  for (const key of leaveKept) replaced.delete(key);
  return { replaced: replaced.size, leaveKept: leaveKept.size };
}

// ===========================================================================
// SEGMENT BRUSHES
//
// A rotation pattern lays down cells that no paint chip could reproduce: its
// segments ("On site", "Fly In", "Days Off") were only ever reachable through
// the per-cell modal, and only when the cell's assignment still resolved to a
// pattern. Clear such a cell and there was no way to put it back.
//
// A SegmentBrush is a paintable rendering of one segment. The list is a
// deduplicated union across every pattern available to a site, so the palette
// no longer depends on which pattern a particular assignment happens to carry
// (69 of site 27's assignments carry none at all).
// ===========================================================================

/** One code found on a site's cells that no palette can reproduce. */
export interface UncoveredCode {
  abbreviation: string;
  shiftType: string | null;
  color: string | null;
  count: number;
}

export interface SegmentBrush {
  /** Stable identity for arming and de-duplication. */
  brushKey: string;
  segmentId: string | null;
  segmentName: string | null;
  shiftType: 'work' | 'travel' | 'off';
  color: string | null;
  abbreviation: string | null;
  workShift?: 'day' | 'night';
  hoursPerDay?: number;
  /** Human label for the chip. */
  label: string;
  /** Swatch colour for the chip — never null, unlike `color`. */
  swatchHex: string;
  /** Patterns this brush was found in, for the tooltip. */
  patternNames: string[];
  /** True for the built-in plain-working-day brush below. */
  builtIn?: boolean;
}

/**
 * The plain working day: `shiftType: 'work'` and nothing else.
 *
 * This is NOT a cosmetic addition. visualForShiftDay's work fallback renders
 * exactly this shape as the label "ON" in #3B82F6, and it is the single most
 * common cell on an imported roster — 18,432 of them on site 40 alone. It had
 * no chip anywhere in the planner, so erasing one was a one-way door. That was
 * the reported roll-out blocker.
 *
 * `shiftType` stays a truthy string on purpose: the paint endpoint treats a
 * patch whose values are ALL null/false as an erase (`isClearOp`), and on a
 * cell with no assignment yet that erase reports success while writing
 * nothing. Never let this patch become all-nullish.
 */
export const WORKING_DAY_BRUSH: SegmentBrush = {
  brushKey: 'builtin:work',
  segmentId: null,
  segmentName: null,
  shiftType: 'work',
  color: null,
  abbreviation: null,
  label: 'On (working day)',
  swatchHex: '#3B82F6',
  patternNames: [],
  builtIn: true,
};

/**
 * Patterns available to a site.
 *
 * `applicableSites` is EMPTY on every pattern in production, so an empty list
 * has to mean "every site" — filtering strictly on it would show nothing at
 * all. A pattern already referenced by one of the site's assignments always
 * qualifies regardless.
 */
export function patternsForSite(
  patterns: PlannerPattern[],
  siteCode: string,
  assignments: Array<{ shiftPatternId?: string | null }> = []
): PlannerPattern[] {
  const used = new Set(
    assignments.map(a => String(a.shiftPatternId ?? '')).filter(Boolean)
  );
  return patterns.filter(
    p =>
      p.isActive !== false &&
      (!p.applicableSites?.length ||
        (!!siteCode && p.applicableSites.includes(siteCode)) ||
        used.has(String(p._id)))
  );
}

const SHIFT_TYPE_ORDER: Record<SegmentBrush['shiftType'], number> = {
  work: 0,
  travel: 1,
  off: 2,
};

/**
 * Flatten patterns into a deduplicated brush list, led by the built-in
 * working-day brush.
 *
 * De-duplication is on the VISUAL signature, not the segment id: nine patterns
 * each defining a "Fly In / FI / travel / #FF9FDF" segment must collapse to one
 * chip, or the palette becomes unusable. Patterns that contributed to a brush
 * are collected so the tooltip can say where it came from.
 */
export function segmentBrushesForPatterns(patterns: PlannerPattern[]): SegmentBrush[] {
  const bySignature = new Map<string, SegmentBrush>();

  for (const p of patterns) {
    for (const seg of p.segments ?? []) {
      if (!seg?.shiftType) continue;
      const signature = [
        seg.shiftType,
        (seg.segmentName ?? '').trim().toLowerCase(),
        (seg.abbreviation ?? '').trim().toUpperCase(),
        (seg.color ?? '').toLowerCase(),
      ].join('|');
      const existing = bySignature.get(signature);
      if (existing) {
        if (!existing.patternNames.includes(p.patternName)) {
          existing.patternNames.push(p.patternName);
        }
        continue;
      }
      bySignature.set(signature, {
        brushKey: `${p._id}:${seg.segmentId}`,
        segmentId: seg.segmentId,
        segmentName: seg.segmentName,
        shiftType: seg.shiftType,
        color: seg.color ?? null,
        abbreviation: seg.abbreviation ?? null,
        workShift: seg.shiftType === 'work' ? seg.workShift : undefined,
        hoursPerDay: seg.hoursPerDay,
        label: seg.segmentName || seg.abbreviation || seg.shiftType,
        swatchHex: seg.color || '#3B82F6',
        patternNames: [p.patternName],
      });
    }
  }

  const brushes = Array.from(bySignature.values()).sort((a, b) => {
    const t = SHIFT_TYPE_ORDER[a.shiftType] - SHIFT_TYPE_ORDER[b.shiftType];
    if (t !== 0) return t;
    return a.label.localeCompare(b.label);
  });

  return [WORKING_DAY_BRUSH, ...brushes];
}

/** OFF means not working, so painting a day off also clears its camp, hours, day/night and coding. */
export const OFF_DAY_CLEARS = {
  campCode: null,
  hoursPerDay: null,
  workShift: null,
  timesheetCoding: null,
} as const;

/**
 * The shiftDay patch that paints a brush.
 *
 * Kept beside the brush model so every caller (click paint, drag paint, bulk
 * bar, bulk modal, cell modal) writes an identical cell — a repainted day is
 * byte-identical to one the pattern materialised. `workShift` and `hoursPerDay`
 * are only sent when the segment defines them, so painting a code never
 * silently wipes forecast hours an admin set by hand — except on an off day,
 * which clears them (OFF_DAY_CLEARS).
 */
export function patchForSegmentBrush(brush: SegmentBrush): Partial<PlannerShiftDay> {
  const patch: Record<string, unknown> = {
    leaveCode: null,
    coverageRequired: false,
    shiftType: brush.shiftType,
    segmentId: brush.segmentId,
    segmentName: brush.segmentName,
    color: brush.color,
    abbreviation: brush.abbreviation,
    ...(brush.shiftType === 'off' ? OFF_DAY_CLEARS : {}),
  };
  if (brush.workShift) patch.workShift = brush.workShift;
  if (brush.hoursPerDay != null) patch.hoursPerDay = brush.hoursPerDay;
  return patch as Partial<PlannerShiftDay>;
}

/**
 * Resolve the visual representation for a shift day. Drives both the planner
 * cell colour and the legend chip. Falls back to a neutral grey for off /
 * unknown cells so the grid stays readable when partial data lands.
 */
export function visualForShiftDay(d: PlannerShiftDay): {
  fillHex: string;
  textHex: string;
  shortLabel: string;
  fullLabel: string;
} {
  if (d.leaveCode && LEGEND[d.leaveCode]) {
    const e = LEGEND[d.leaveCode];
    return {
      fillHex: e.fillHex,
      textHex: e.textHex,
      shortLabel: e.shortLabel,
      fullLabel: e.fullLabel,
    };
  }

  // Abbreviation matches a legend code — give the cell the legend's
  // colours regardless of how it got onto the shiftDay (manual paint
  // OR a rotation-pattern segment). That way an OS day from a pattern
  // uses the same green + dark text as the manual paint chip, instead
  // of inheriting whatever colour the pattern segment was authored
  // with (which tended to be a brighter green with white text and
  // looked inconsistent next to manually-painted cells).
  //
  // Falls through for non-legend abbreviations (camp codes like MOO /
  // WGU / UBI etc.), which keep the pattern's segment colour below.
  if (d.abbreviation) {
    const legendKey = d.abbreviation as keyof typeof LEGEND;
    if (LEGEND[legendKey]) {
      const e = LEGEND[legendKey];
      return {
        fillHex: e.fillHex,
        textHex: e.textHex,
        shortLabel: e.shortLabel,
        fullLabel: e.fullLabel,
      };
    }
  }

  // Work segment — use the segment's own colour (set by the pattern) so a
  // site's branding shows through. Abbreviation = the camp code in Petta's
  // sheet (MOO / WGU / UBI / NUG ...). Default to a soft blue if absent.
  if (d.shiftType === 'work') {
    return {
      fillHex: d.color || '#3B82F6',
      textHex: '#FFFFFF',
      shortLabel: d.abbreviation || 'ON',
      fullLabel: d.segmentName || 'Working',
    };
  }

  if (d.shiftType === 'travel') {
    return {
      fillHex: d.color || '#F59E0B',
      textHex: '#1F2937',
      shortLabel: d.abbreviation || 'TVL',
      fullLabel: d.segmentName || 'Travel',
    };
  }

  // Off / unknown — render with no fill so the cell visually recedes.
  return { fillHex: 'transparent', textHex: '#9CA3AF', shortLabel: '', fullLabel: 'Off' };
}

/**
 * Transform a flat list of RosterAssignments into Planby's channels + epg.
 * One channel per employee in the result set; one program per shiftDay.
 * Programs span midnight-to-midnight so the grid renders as day cells.
 *
 * If `siteEmployees` is provided (the full HRPerson roster for the site),
 * every one of them gets a channel even if they have no assignment — so
 * the planner shows blank rows ready for cell painting, Excel-style. The
 * assignment-derived channels still win for display info (swing team,
 * room number), keeping the planner consistent for rostered employees.
 */
export function assignmentsToEpg(
  assignments: PlannerAssignment[],
  employees: PlannerEmployee[],
  siteEmployees?: PlannerEmployee[],
  rangeStart?: Date,
  rangeEnd?: Date
): { channels: PlannerChannel[]; epg: PlannerProgram[] } {
  // Only build programs for days within the visible window (with a 1-day pad).
  // Imported / merged assignments can carry 800+ shiftDays spanning years;
  // building a program for every one of them froze the planner. Days outside
  // the window are never rendered, so skipping them is purely a performance win
  // — the full shiftDays array stays on the assignment for index-based writes.
  const loMs = rangeStart ? rangeStart.getTime() - 24 * 60 * 60 * 1000 : -Infinity;
  const hiMs = rangeEnd ? rangeEnd.getTime() + 24 * 60 * 60 * 1000 : Infinity;
  const employeesById = new Map<string, PlannerEmployee>();
  employees.forEach(e => employeesById.set(e.employeeId, e));
  if (siteEmployees) {
    siteEmployees.forEach(e => {
      if (!employeesById.has(e.employeeId)) employeesById.set(e.employeeId, e);
    });
  }

  const channelByEmpId = new Map<string, PlannerChannel>();
  const epg: PlannerProgram[] = [];

  // Helper to build / upgrade a channel for an employee.
  function ensureChannel(employeeId: string, fromAssignment?: PlannerAssignment) {
    const existing = channelByEmpId.get(employeeId);
    const emp = employeesById.get(employeeId);
    const displayName =
      emp?.displayName ||
      [emp?.firstName, emp?.lastName].filter(Boolean).join(' ') ||
      employeeId;
    const subtitleParts = [
      emp?.position,
      fromAssignment?.swingTeam ? `Team ${fromAssignment.swingTeam}` : null,
    ].filter(Boolean) as string[];
    const channel: PlannerChannel = {
      uuid: employeeId,
      title: displayName,
      subtitle: subtitleParts.join(' · ') || undefined,
      team: fromAssignment?.swingTeam,
      position: emp?.position,
      roomNo: fromAssignment?.permanentRoomNo,
      // A person with an assignment is inherently a member; otherwise fall back
      // to the site-employee membership flag (R1).
      isMember: emp?.isMember ?? !!fromAssignment,
    };
    // If a channel was already created from the site-roster list (no
    // assignment), prefer the richer assignment-derived version on second
    // pass so swingTeam / roomNo land in the sidebar.
    if (!existing || (fromAssignment && !existing.team)) {
      channelByEmpId.set(employeeId, channel);
    }
  }

  // Pass 1 — every assigned employee gets a row + cells.
  for (const a of assignments) {
    ensureChannel(a.employeeId, a);
    for (const d of a.shiftDays || []) {
      // Skip days outside the visible window — they're never rendered. Tested
      // before a Date is built: most of a long assignment's days are skipped.
      // A missing or unreadable date (NaN) is skipped too, not drawn.
      const t = Date.parse(d.date);
      if (!(t >= loMs && t <= hiMs)) continue;
      const since = new Date(t);
      const till = new Date(t + 24 * 60 * 60 * 1000);
      const v = visualForShiftDay(d);
      epg.push({
        id: `${a._id}::${ymdKey(since)}`,
        channelUuid: a.employeeId,
        title: v.shortLabel,
        since: since.toISOString(),
        till: till.toISOString(),
        shiftDay: d,
        assignmentId: a._id,
        status: a.status,
      });
    }
  }

  // Pass 2 — every site employee who hasn't been added yet gets a blank
  // row. The "Excel feel": admins see EVERYONE at their site and start
  // painting, the assignment is lazy-created on first paint.
  if (siteEmployees) {
    for (const e of siteEmployees) {
      if (!channelByEmpId.has(e.employeeId)) ensureChannel(e.employeeId);
    }
  }

  const channels = Array.from(channelByEmpId.values());
  channels.sort((x, y) => x.title.localeCompare(y.title));
  return { channels, epg };
}

/**
 * Synthesise placeholder "blank" programs for every (employee × day) cell
 * in the visible range that doesn't already have a real program. The
 * placeholders give Planby something to render at every grid intersection
 * so the planner becomes a true Excel-style matrix — every cell is a
 * drop target for legend chips, and admins can paint any day for any
 * employee at the site without first "adding them to roster".
 *
 * Placeholder programs:
 *   - id: `blank::${employeeId}::${YYYY-MM-DD}` (distinguishable from real
 *     `${assignmentId}::${date}` ids)
 *   - assignmentId: '' (paint endpoint will lazy-create on drop)
 *   - shiftDay.shiftType: 'off' (renders as near-transparent in visualForShiftDay)
 *
 * Real programs always win — we only emit a blank where no real cell
 * exists for that exact employee+date combination.
 */
export function synthesiseBlankCells(
  channels: PlannerChannel[],
  realPrograms: PlannerProgram[],
  rangeStart: Date,
  rangeEnd: Date
): PlannerProgram[] {
  // Build a set of (channelUuid, dateKey) we already have. Use the local
  // ymdKey so this lines up with the matrix's cell-key generation and the
  // paint-time dayKey extraction.
  const occupied = new Set<string>();
  for (const p of realPrograms) {
    const key = `${p.channelUuid}::${ymdKey(p.since)}`;
    occupied.add(key);
  }

  // Each day's key and timestamps, worked out once per day rather than once per
  // person: a Year on a big site is tens of thousands of placeholders.
  const days = enumerateDays(rangeStart, rangeEnd).map(d => ({
    key: ymdKey(d),
    since: d.toISOString(),
    till: new Date(d.getTime() + 24 * 60 * 60 * 1000).toISOString(),
  }));

  const blanks: PlannerProgram[] = [];
  for (const ch of channels) {
    for (const day of days) {
      if (occupied.has(`${ch.uuid}::${day.key}`)) continue;
      blanks.push({
        id: `blank::${ch.uuid}::${day.key}`,
        channelUuid: ch.uuid,
        title: '',
        since: day.since,
        till: day.till,
        shiftDay: { date: day.since, shiftType: 'off' },
        assignmentId: '',
        status: 'draft',
      });
    }
  }
  return blanks;
}
